/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IMenuItemData, INavigationMenuViewProps } from '@msdyn365-commerce-modules/navigation-menu';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

interface INavigationState {
  parentMenu?: number;
  activeMenu?: number;
  mobileViewLabelText?: string;
  navOpen: boolean;
}
/**
 *
 * NavigationMenuView component
 * @extends {React.PureComponent<INavigationMenuViewProps>}
 */
export class NavigationMenuView extends React.PureComponent<INavigationMenuViewProps, INavigationState> {
  private supportedLevel: number = 3;
  private currentLevel: number = 0;
  private menuNode: React.RefObject<HTMLUListElement>;
  private pathName: string = '';
  private activeClassName: string = 'selected';
  private activeMenuItem: IMenuItemData = {};
  private activeSubMenuItem: IMenuItemData = {};
  constructor(props: INavigationMenuViewProps) {
    super(props);
    this.menuNode = React.createRef();
    this.state = { activeMenu: undefined, mobileViewLabelText: '', parentMenu: undefined, navOpen: false };
    this._closeSubmenu = this._closeSubmenu.bind(this);
    this._onMobNavTrigger = this._onMobNavTrigger.bind(this);
  }

  public componentDidMount(): void {
    document.body && document.body.addEventListener('click', this._handleClickOutside);
    document.body && document.body.addEventListener('keydown', this.trapFocusInsideMenu);
  }
  public componentWillUnmount(): void {
    document.body && document.body.removeEventListener('click', this._handleClickOutside, false);
    document.body && document.body.removeEventListener('keydown', this.trapFocusInsideMenu, false);
  }
  public render(): JSX.Element | null {
    const { isMobileView, MenuList, Navigation } = this.props;
    // get current pathname
    if (process.env.CURRENT_ENVIRONMENT === 'web') {
      this.pathName = window.location.pathname;
    } else {
      this.pathName = this.props.context.request.url.requestUrl.pathname;
    }
    this.props.menuItemData.forEach((item, index) => {
      item.subMenu && item.subMenu.forEach((subItem, subIndex) => {
        const subItemPath = this._getPathNameFromURL(subItem.linkURL || '');
        if (subItemPath === this.pathName) {
          this.activeMenuItem = item;
          this.activeSubMenuItem = subItem;
        }
      });
    });
    this.currentLevel = 1;
    return (
      <React.Fragment>
        <button
          onClick={this._onMobNavTrigger}
          className='ms-header__nav-icon msc-btn'
          aria-label='Menu'
          aria-expanded={this.state.navOpen ? 'true' : 'false'}
          aria-haspopup='true'
        />
        <Module {...Navigation} className={classnames(Navigation.className, isMobileView && this.state.activeMenu !== undefined ? 'child' : 'parent', this.state.navOpen ? 'nav-open' : 'nav-closed')}>
          <Node {...MenuList} ref={this.menuNode} role='menu'>
            {this._renderDisplay()}
          </Node>
        </Module>
      </React.Fragment>
    );
  }

  private _getPathNameFromURL(linkURL: string): string {
    let pathName = '';
    try {
      pathName = new URL(linkURL).pathname;
    } catch {
      pathName = linkURL.split('?')[0];
    }
    return pathName;
  }
  private _onMobNavTrigger(): void {
    this.setState(state => ({
      navOpen: !state.navOpen
    }));
  }

  private _renderDisplay(): JSX.Element[] {
    const { ListItem, menuItemData } = this.props;
    const menuItemList: JSX.Element[] = [];
    menuItemData.forEach((item: IMenuItemData, index: number) => {
      menuItemList.push(
        <Node key={index} {...ListItem} role='menuitem'>
          {this._createMenuItemList(item)}
        </Node>
      );
    });

    return menuItemList;
  }

  private _createMenuItemList(menuItemData: IMenuItemData): JSX.Element | null {
    if (menuItemData && menuItemData.subMenu && menuItemData.subMenu.length > 0) {
      return (
        <React.Fragment>
          {this._renderButtonMenuItem(menuItemData)}
          <Node className={`collapse subnav-menu ${menuItemData.id === this.state.activeMenu ? 'subnav-show' : 'subnav-hide'}`}>
            {this._renderSubMenu(menuItemData.subMenu, menuItemData.id)}
          </Node>
        </React.Fragment>
      );
    } else if (menuItemData && menuItemData.linkText && menuItemData.linkURL && menuItemData.linkURL.length > 0) {
      return (this._renderLinkMenuItem(menuItemData));
    } else if (menuItemData && menuItemData.linkText && !menuItemData.linkURL) {
      return (this._renderSpanMenuItem(menuItemData));
    }

    return null;
  }

  private _renderSubMenu(subMenus?: IMenuItemData[], activeMenu?: number): JSX.Element | null {
    const { isMobileView, ListItem } = this.props;
    if (activeMenu && this.state.activeMenu !== activeMenu && !isMobileView) {
      return null;
    }

    if (!subMenus || subMenus.length === 0) {
      return null;
    }

    let levelClassName: string = '';
    const menuOptions = subMenus && subMenus.map((option: IMenuItemData, idx: number) => {
      const hasOptions = (option.subMenu && option.subMenu.length > 0);
      let menuItem: JSX.Element | null;
      if (hasOptions && isMobileView) {
        menuItem = this._renderButtonMenuItem(option, activeMenu, idx);
      } else {
        menuItem = (option.linkURL
          ? this._renderLinkMenuItem(option, idx)
          : this._renderSpanMenuItem(option)
        );
      }

      let subMenu;

      if (hasOptions) {
        this.currentLevel++;
        if (this.currentLevel <= this.supportedLevel - 1) {
          levelClassName = `level-${this.currentLevel.toString()}`;
          subMenu = this._renderSubMenu(option.subMenu, isMobileView ? option.id : undefined);
        }
      }

      return (
        <Node {...ListItem} key={option.id}>
          {menuItem}
          {subMenu}
        </Node>
      );
    });

    return (this._renderMenu(levelClassName, menuOptions, activeMenu));
  }

  private _renderButtonMenuItem(option: IMenuItemData, activeMenu?: number, index?: number): JSX.Element | null {
    const { Button } = this.props;
    const isActive = option === this.activeMenuItem || option === this.activeSubMenuItem;
    const activeClass = `${Button.className} ${isActive ? this.activeClassName : ''}`;

    return (
      <Node
        key={index}
        {...Button}
        className={activeClass}
        onClick={this._handleDropdownToggle(option, activeMenu, 'onClick')}
        onFocus={this._handleDropdownToggle(option, activeMenu, 'onFocus')}
        aria-haspopup={true}
        aria-expanded={this.state.activeMenu && this.state.activeMenu === option.id ? true : false}
        data-parent={activeMenu}
        aria-current={isActive && 'page'}
      >
        {option.linkText}
      </Node>
    );
  }

  private _renderLinkMenuItem(option: IMenuItemData, index?: number): JSX.Element | null {
    const { Link } = this.props;
    const isActive = option === this.activeSubMenuItem;
    const activeClass = `${Link.className} ${isActive ? this.activeClassName : ''}`;

    return (
      <Node {...Link} className={activeClass} key={index} href={option.linkURL} aria-current={isActive && 'page'}>
        {option.linkText}
      </Node>
    );
  }

  private _renderSpanMenuItem(option: IMenuItemData, index?: number): JSX.Element | null {
    const { Span } = this.props;
    return (
      <Node key={index} {...Span}>{option.linkText}</Node>
    );
  }

  private _renderMenu(level: string, menuOptions: JSX.Element[], currentItem?: number): JSX.Element | null {
    const { MenuList } = this.props;
    this.currentLevel = 1;
    return (
      <Node {...MenuList} className={classnames(MenuList.className, level)}>
        {menuOptions}
      </Node>
    );
  }

  private _handleDropdownToggle = (data: IMenuItemData, parentId?: number, type?: string) => () => {
    if (this.props.isMobileView && type === 'onFocus') {
      null;
    } else {
      let activeMenu = data.id;
      if (this.props.isMobileView) {
        activeMenu = (this.state.activeMenu && this.state.activeMenu === data.id!) ? undefined : data.id!;
      }

      this.setState({
        activeMenu,
        parentMenu: parentId
      });

      this._resetFocus();
    }
  };

  private _resetFocus = () => {
    if (this.props.isMobileView) {
      setTimeout(() => {
        this.menuNode && this.menuNode.current && this.menuNode.current.focus();
      }, 0);
    }
  };

  // tslint:disable-next-line:no-any
  private _handleClickOutside = (event: any) => {
    const burgerMenu: boolean = event.target.className === 'ms-header__nav-icon msc-btn';
    if (this.menuNode.current && !this.menuNode.current.contains(event.target) && !burgerMenu) {
      this.setState({ navOpen: false });
    }
    this._resetFocus();
  };

  private _closeSubmenu(): void {
    if (!this.props.isMobileView) {
      this.setState({ activeMenu: undefined, mobileViewLabelText: '' });
    }
  }

  private trapFocusInsideMenu = (event: any) => {
    const isTabPressed = event.key === 'Tab';
    if (!isTabPressed) {
      return;
    }
    //move forward only if tab key is pressed
    const firstFocusableElement: HTMLElement | null = document.getElementsByClassName("ms-header__nav-icon msc-btn").length > 0 ? document.getElementsByClassName("ms-header__nav-icon msc-btn")[0] as HTMLElement : null;
    if (document.getElementsByClassName("nav-open").length === 0) {
      //Let's return If the navbar is not open
      return;
    }
    else {
      let navOpen = document.getElementsByClassName("nav-open")[0];
      let lastFocusableElement = null;
      //This means the nav bar is open
      //lets trap the focus inside the navbar
      while (navOpen.children.length > 0) {
        const msNavList = navOpen.children;
        if (msNavList.length === 1 && msNavList[0].tagName === 'UL') {
          const msNavListItem = msNavList[0].children;
          if (msNavListItem.length > 0) {
            //we have menu items list with us in msNavListItem
            const lastMenuItem = msNavListItem[msNavListItem.length - 1];
            const submenu = Array.from(lastMenuItem.children);
            const subnavShow = submenu.find(s => s.classList.contains("subnav-show"));
            if (subnavShow) {
              //if we are here then the last menu is open.
              //Check for inner submenus
              navOpen = subnavShow;
            }
            else if (submenu[submenu.length - 1].tagName === 'A' && submenu[submenu.length - 1].className.includes("ms-nav__list__item__link")) {
              //This means we are at the last menu and there is no further submenu inside it.
              lastFocusableElement = submenu[submenu.length - 1];
              break;
            }
            else {
              //If we are here then the submenu is not open
              lastFocusableElement = Array.from(lastMenuItem.children).find(b=>b.classList.contains("ms-nav__list__item__button"));
              break;
            }
          }
        }
      }
      //We need to see if the focus is on the last sub menu item if so then make the focus to menu open button, Doing so we are traping the focus
      //inside the menu.
      if (document.activeElement === lastFocusableElement && firstFocusableElement) {
        firstFocusableElement.focus();
        event.preventDefault();
      }
    }
  };
}

export default NavigationMenuView;

